import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { View, StyleSheet, FlatList, KeyboardAvoidingView, Platform, ActivityIndicator, Modal, TouchableOpacity, Text, Alert, TextInput } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import TalkHeader from '../components/TalkHeader';
import MessageBubble from '../components/MessageBubble';
import DateSeparator from '../components/DateSeparator';
import InputBar, { InputBarHandle } from '../components/InputBar';
import { Message } from '../types';
import { getDid, createPost, getChatMessages, sendConvoMessage } from '../services/bluesky';
import { Theme, useThemedStyles, useTheme, useFontSize } from '../theme';

interface Props {
  room: {
    id: string;
    title: string;
    type: 'following' | 'feed' | 'list' | 'convo';
    uri?: string;
    otherDid?: string;
  };
  onBack: () => void;
  onSelectUser?: (did: string) => void;
  onSelectPost?: (post: any) => void;
  onSearchHashtag?: (hashtag: string) => void;
  onSearchHandle?: (handle: string) => void;
  onSettings?: () => void;
}

const TalkRoom: React.FC<Props> = ({ room, onBack, onSelectUser, onSelectPost, onSearchHashtag, onSearchHandle, onSettings }) => {
  const SCROLL_TO_LATEST_DELAY_MS = 100; // Delay to ensure FlatList is rendered before scrolling

  type ChatItem =
    | { type: 'separator'; id: string; date: string }
    | { type: 'message'; id: string; message: Message }
    | { type: 'loading'; id: string };

  const formatDateLabel = (date: string): string => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return date;
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    const dd = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}/${mm}/${dd}`;
  };

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [menuVisible, setMenuVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isInitialScrollDone, setIsInitialScrollDone] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string>('');
  const [showMediaOnly, setShowMediaOnly] = useState(false);
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const lastAutoRefreshRef = useRef<number>(0);
  const [replyTarget, setReplyTarget] = useState<Message | null>(null);
  const [quoteTarget, setQuoteTarget] = useState<Message | null>(null);
  const isFirstLoadRef = useRef(true);
  const insets = useSafeAreaInsets();
  const myDid = getDid();
  const { t } = useTranslation();
  const CACHE_KEY_MESSAGES = `cache_messages_${room.id}_${myDid}`;
  const CACHE_KEY_CURSOR = `cache_cursor_${room.id}_${myDid}`;
  const CACHE_KEY_BACKGROUND = `cache_background_${room.id}`;
  const listRef = useRef<FlatList>(null);
  const inputBarRef = useRef<InputBarHandle>(null);
  const { theme } = useTheme();
  const { fontSizeScale } = useFontSize();
  const styles = useThemedStyles(createStyles);

  // Scroll to latest when messages first load
  useEffect(() => {
    if (!loading && messages.length > 0 && isFirstLoadRef.current) {
      // Wait for FlatList to render
      setTimeout(() => {
        if ((listRef.current as any)?.scrollToEnd) {
          try {
            (listRef.current as any).scrollToEnd({ animated: false });
          } catch {
            listRef.current?.scrollToOffset({ offset: 99999, animated: false });
          }
        }
        
        // Double-check after images might load
        setTimeout(() => {
          if ((listRef.current as any)?.scrollToEnd) {
            try {
              (listRef.current as any).scrollToEnd({ animated: false });
            } catch {
              listRef.current?.scrollToOffset({ offset: 99999, animated: false });
            }
          }
          isFirstLoadRef.current = false;
          setIsInitialScrollDone(true);

          // If the first page doesn't fill the screen, preload older messages
          // so infinite scroll still works without any scroll gesture.
          if (cursor && !loadingMore && !loading && contentHeightRef.current <= layoutHeightRef.current + 50) {
            loadMore();
          }
        }, 200);
      }, 100);
    }
  }, [loading, messages.length]);

  const handleHashtagPress = useCallback((hashtag: string) => {
    if (inputBarRef.current) {
      inputBarRef.current.appendText(hashtag);
    }
  }, []);
  const lastOffsetRef = useRef<number>(0);
  const contentHeightRef = useRef<number>(0);
  const prevContentHeightRef = useRef<number>(0);
  const offsetBeforeUpdateRef = useRef<number>(0);
  const willAdjustRef = useRef<boolean>(false);
  const contentOffsetRef = useRef<number>(0);
  const layoutHeightRef = useRef<number>(0);

  const fetchTimeline = useCallback(async (isLoadingNewer = false) => {
    if (isLoadingNewer) setLoadingNewer(true);
    setIsFetching(true);
    try {
      const { messages: mappedMessages, cursor: nextCursor } = await getChatMessages(
        room.type,
        room.uri,
        undefined,
        myDid
      );
      
      setMessages(prev => {
        // On first load (when prev is empty), don't merge - just use new messages
        if (prev.length === 0) {
          AsyncStorage.setItem(CACHE_KEY_MESSAGES, JSON.stringify(mappedMessages));
          return mappedMessages;
        }
        
        // Merge new messages with existing ones, removing duplicates
        const combined = [...mappedMessages, ...prev];
        const unique = combined.filter((msg, index, self) =>
          index === self.findIndex((m) => m.cid === msg.cid)
        );
        
        // Sort by indexedAt descending (latest first)
        unique.sort((a, b) => {
          const timeA = new Date(a.indexedAt || a.createdAt || 0).getTime();
          const timeB = new Date(b.indexedAt || b.createdAt || 0).getTime();
          return timeB - timeA;
        });

        // Update cache with all messages
        AsyncStorage.setItem(CACHE_KEY_MESSAGES, JSON.stringify(unique));
        return unique;
      });

      if (nextCursor) {
        setCursor(nextCursor);
        await AsyncStorage.setItem(CACHE_KEY_CURSOR, nextCursor);
      }
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setLoading(false);
      setIsFetching(false);
      setLoadingNewer(false);
    }
  }, [room, myDid, CACHE_KEY_MESSAGES, CACHE_KEY_CURSOR]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !cursor) {
      return;
    }
    
    setLoadingMore(true);
    try {
      const { messages: mappedMessages, cursor: nextCursor } = await getChatMessages(
        room.type,
        room.uri,
        cursor,
        myDid
      );
      
      if (!mappedMessages || mappedMessages.length === 0) {
        setLoadingMore(false);
        willAdjustRef.current = false;
        setCursor(undefined);
        return;
      }
      
      // Build new message list
      const combined = [...messages, ...mappedMessages];
      const unique = combined.filter((msg, index, self) => index === self.findIndex((m) => m.cid === msg.cid));
      unique.sort((a, b) => {
        const timeA = new Date(a.indexedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.indexedAt || b.createdAt || 0).getTime();
        return timeB - timeA;
      });

      if (unique.length === messages.length) {
        if (nextCursor && nextCursor !== cursor) {
          setCursor(nextCursor);
          await AsyncStorage.setItem(CACHE_KEY_CURSOR, nextCursor);
        } else {
          setCursor(undefined);
        }
        willAdjustRef.current = false;
        setLoadingMore(false);
        return;
      }

      // Prepare to adjust scroll BEFORE setting messages
      prevContentHeightRef.current = contentHeightRef.current;
      offsetBeforeUpdateRef.current = contentOffsetRef.current;
      willAdjustRef.current = true;

      // Persist and set state
      AsyncStorage.setItem(CACHE_KEY_MESSAGES, JSON.stringify(unique));
      setMessages(unique);

      if (nextCursor && nextCursor !== cursor) {
        setCursor(nextCursor);
        await AsyncStorage.setItem(CACHE_KEY_CURSOR, nextCursor);
      } else {
        setCursor(undefined);
      }
    } catch (error) {
      console.error('Failed to load more:', error);
      willAdjustRef.current = false;
      setLoadingMore(false);
    } finally {
      // Keep loadingMore true until scroll adjustment is complete
    }
  }, [cursor, loadingMore, room, myDid, CACHE_KEY_MESSAGES, CACHE_KEY_CURSOR, messages]);

  const handleScroll = useCallback((e: any) => {
    try {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const y = contentOffset.y || 0;
      contentOffsetRef.current = y;
      const THRESHOLD = 100; // px from edge
      const COOLDOWN = 10000; // ms

      // Determine movement direction
      const isMovingUp = y < lastOffsetRef.current; 
      const isMovingDown = y > lastOffsetRef.current;
      
      // Update lastOffsetRef only if we are NOT in the middle of a coordinate adjustment
      if (!willAdjustRef.current) {
        lastOffsetRef.current = y;
      }

      // In non-inverted FlatList: y=0 is the top (oldest messages)
      const isAtTop = y < THRESHOLD;
      const isAtBottom = (y + layoutMeasurement.height) > (contentSize.height - THRESHOLD);

      setShowScrollToBottom(!isAtBottom);

      // When user scrolls to top, load older messages
      if (isMovingUp && isAtTop && !loadingMore && cursor) {
        loadMore();
      }

      // When user scrolls to bottom, fetch new messages if cooldown passed
      if (isMovingDown && isAtBottom && !isFetching && !loadingNewer && Date.now() - lastAutoRefreshRef.current > COOLDOWN) {
        lastAutoRefreshRef.current = Date.now();
        fetchTimeline(true);
      }
    } catch (err) {
      // ignore
    }
  }, [isFetching, fetchTimeline, loadingMore, loadingNewer, cursor, loadMore]);

  const handleSend = useCallback(async (text: string, media?: { uri: string; mimeType: string }[]) => {
    try {
      if (room.type === 'convo') {
        await sendConvoMessage(room.uri!, text);
      } else {
        let reply = undefined;
        if (replyTarget) {
          const root = replyTarget.replyRoot || { uri: replyTarget.uri, cid: replyTarget.cid };
          reply = {
            root: root,
            parent: { uri: replyTarget.uri, cid: replyTarget.cid },
          };
        }

        const result = await createPost({ 
          text, 
          media, 
          reply,
          quote: quoteTarget ? { uri: quoteTarget.uri, cid: quoteTarget.cid } : undefined
        });
        
        // Immediately add the new post to the message list
        if (result?.data) {
          const post = result.data;
          const now = new Date().toISOString();
          
          // Use uploaded media URLs from the result
          const uploadedImages = result.uploadedMedia?.images || [];
          const uploadedVideo = result.uploadedMedia?.video;
          
          const newMessage: Message = {
            id: post.cid,
            uri: post.uri,
            cid: post.cid,
            text: text,
            createdAt: new Date(now).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            date: now.split('T')[0],
            indexedAt: now,
            isMine: true,
            authorDid: myDid,
            senderName: 'You',
            avatarUrl: undefined,
            images: uploadedImages.length > 0 ? uploadedImages : undefined,
            video: uploadedVideo,
            replyParent: replyTarget ? {
              uri: replyTarget.uri,
              cid: replyTarget.cid,
              senderName: replyTarget.senderName || 'Unknown',
              text: replyTarget.text,
              avatarUrl: replyTarget.avatarUrl,
              author: replyTarget.author,
              record: replyTarget.record,
            } : undefined,
            quotedPost: quoteTarget ? {
              uri: quoteTarget.uri,
              cid: quoteTarget.cid,
              senderName: quoteTarget.senderName || 'Unknown',
              text: quoteTarget.text,
              avatarUrl: quoteTarget.avatarUrl,
              author: quoteTarget.author,
              record: quoteTarget.record,
            } : undefined,
          };
          
          setMessages(prev => {
            const updated = [newMessage, ...prev];
            AsyncStorage.setItem(CACHE_KEY_MESSAGES, JSON.stringify(updated));
            return updated;
          });
          
          // Scroll to latest (bottom)
          setTimeout(() => {
            if ((listRef.current as any)?.scrollToEnd) {
              try {
                (listRef.current as any).scrollToEnd({ animated: true });
              } catch {
                listRef.current?.scrollToOffset({ offset: 99999, animated: true });
              }
            } else {
              listRef.current?.scrollToOffset({ offset: 99999, animated: true });
            }
          }, 100);
        }
      }
      
      setReplyTarget(null);
      setQuoteTarget(null);
      // Refresh timeline after a delay to get the indexed version
      setTimeout(() => fetchTimeline(), 2000);
    } catch (error) {
      console.error('Failed to post:', error);
    }
  }, [replyTarget, quoteTarget, fetchTimeline, room, myDid, CACHE_KEY_MESSAGES]);

  const loadCache = useCallback(async () => {
    try {
      const cachedMessages = await AsyncStorage.getItem(CACHE_KEY_MESSAGES);
      const cachedCursor = await AsyncStorage.getItem(CACHE_KEY_CURSOR);
      
      if (cachedMessages) {
        const parsed = JSON.parse(cachedMessages);
        setMessages(parsed);
        setLoading(false); // Show cached data immediately
      } else {
        setMessages([]);
        setLoading(true);
      }
      if (cachedCursor) {
        setCursor(cachedCursor);
      } else {
        setCursor(undefined);
      }
    } catch (error) {
      console.error('Failed to load cache:', error);
    }
  }, [CACHE_KEY_MESSAGES, CACHE_KEY_CURSOR]);

  useEffect(() => {
    // Reset state when room changes
    isFirstLoadRef.current = true;
    setIsInitialScrollDone(false);
    setMessages([]);
    setLoading(true);
    
    // Load background color
    AsyncStorage.getItem(CACHE_KEY_BACKGROUND).then(color => {
      if (color) setBackgroundColor(color);
    });
    
    // Skip cache and fetch fresh data directly
    fetchTimeline();
  }, [room.id]); // Re-run when room changes

  const data = useMemo(() => {
    const filteredMessages = isSearchActive && searchQuery
      ? messages.filter(m => m.text?.toLowerCase().includes(searchQuery.toLowerCase()))
      : messages;

    // Filter for media only if enabled
    const finalMessages = showMediaOnly
      ? filteredMessages.filter(m => m.images || m.video)
      : filteredMessages;

    // messages is [latest, ..., oldest]
    const result: (ChatItem)[] = [];
    let lastDate = '';

    finalMessages.forEach((msg, idx) => {
      const date = msg.date;
      if (lastDate && date !== lastDate) {
        // Use the message cid in the separator id to make it stable
        result.push({ type: 'separator', id: `sep-${date}-${msg.cid || msg.id}`, date: lastDate });
      }
      result.push({ type: 'message', id: msg.id || `${msg.uri}-${msg.repostedBy || ''}`, message: msg });
      lastDate = date;
    });
    
    if (lastDate && finalMessages.length > 0) {
      const lastMsg = finalMessages[finalMessages.length - 1];
      result.push({ type: 'separator', id: `sep-${lastDate}-${lastMsg.cid || lastMsg.id}-last`, date: lastDate });
    }

    // Add loading indicator as an item at the 'top' (end of array before reverse)
    if (loadingMore) {
      result.push({ type: 'loading', id: 'loading-more-top' });
    }

    // For non-inverted FlatList (top=oldest, bottom=latest) we need to return oldest->latest order
    return [...result].reverse();
  }, [messages, isSearchActive, searchQuery, showMediaOnly, loadingMore]);

  const renderItem = useCallback(({ item }: { item: ChatItem }) => {
    if (item.type === 'loading') {
      return (
        <View style={{ paddingVertical: 20 }}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      );
    }
    if (item.type === 'separator') {
      return (
        <DateSeparator label={formatDateLabel(item.date)} />
      );
    }
    return (
      <MessageBubble 
        message={item.message} 
        onReply={setReplyTarget}
        onQuote={setQuoteTarget}
        onActionComplete={fetchTimeline}
        onSelectUser={onSelectUser}
        onHashtagPress={handleHashtagPress}
        onSearchHashtag={onSearchHashtag}
        onHandlePress={onSearchHandle}
        onSelectPost={onSelectPost}
        fontSizeScale={fontSizeScale}
      />
    );
  }, [fetchTimeline, onSelectUser, handleHashtagPress, onSearchHashtag, onSelectPost]);

  const handleSearchInRoom = () => {
    setMenuVisible(false);
    setIsSearchActive(true);
  };

  return (
    <View style={styles.container}>
      {/* Status Bar Background */}
      <View style={[styles.fixedTopSpacer, { height: insets.top }]} />
      
      {isSearchActive ? (
        <View style={[styles.searchHeader, { paddingLeft: insets.left, paddingRight: insets.right }]}>
          <TouchableOpacity 
            style={styles.searchBack} 
            onPress={() => {
              setIsSearchActive(false);
              setSearchQuery('');
            }}
          >
            <Ionicons name="chevron-back" size={28} color="white" />
          </TouchableOpacity>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={18} color={theme.colors.textMuted} style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder={t('talkRoom.searchPlaceholder')}
              placeholderTextColor={theme.colors.textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoFocus
            />
            {searchQuery ? (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={18} color={theme.colors.textMuted} />
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      ) : (
        <View style={[styles.headerShell, { paddingLeft: insets.left, paddingRight: insets.right }]}>
          <TalkHeader 
            title={room.title} 
            onBack={onBack} 
            onMenuPress={() => setMenuVisible(true)}
            onSearchPress={() => setIsSearchActive(true)}
          />
        </View>
      )}
      
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={0}
      >
        <View style={[styles.chatArea, { backgroundColor: backgroundColor || theme.colors.background }]}>        
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
          ) : (
            <FlatList
              ref={listRef}
              style={styles.messageScroller}
              data={data}
              keyExtractor={(item) => item.id}
              renderItem={renderItem}
              contentContainerStyle={[
                styles.messageList,
                { 
                  paddingLeft: Math.max(insets.left, 8), 
                  paddingRight: Math.max(insets.right, 8) 
                }
              ]}
              inverted={false}
              onLayout={(e) => { layoutHeightRef.current = e.nativeEvent.layout.height; }}
              maintainVisibleContentPosition={Platform.OS === 'web' ? undefined : { minIndexForVisible: 1 }}
              onEndReached={undefined}
              onEndReachedThreshold={0.5}
              onScroll={handleScroll}
              scrollEventThrottle={16}
              ListHeaderComponent={null}
              ListFooterComponent={
                loadingNewer ? (
                  <View style={{ paddingVertical: 20, alignItems: 'center' }}>
                    <ActivityIndicator color={theme.colors.primary} />
                  </View>
                ) : null
              }
              onContentSizeChange={(w, h) => {
                contentHeightRef.current = h;

                // On native, maintainVisibleContentPosition keeps the reading
                // position when older messages are prepended.
                if (Platform.OS !== 'web') {
                  if (willAdjustRef.current) {
                    setLoadingMore(false);
                    willAdjustRef.current = false;
                  }
                  return;
                }

                // Keep the same messages visible when older items are prepended (web fallback).
                if (willAdjustRef.current) {
                  const delta = h - prevContentHeightRef.current;

                  if (delta > 10) {
                    const newOffset = offsetBeforeUpdateRef.current + delta;

                    requestAnimationFrame(() => {
                      if (listRef.current) {
                        listRef.current.scrollToOffset({
                          offset: newOffset,
                          animated: false
                        });

                        contentOffsetRef.current = newOffset;
                        lastOffsetRef.current = newOffset;
                      }

                      setLoadingMore(false);
                      willAdjustRef.current = false;
                      prevContentHeightRef.current = h;
                    });
                  } else {
                    setLoadingMore(false);
                    willAdjustRef.current = false;
                  }
                }
              }}
            />
          )}
        </View>
        
        {/* Scroll to Bottom Button */}
        {showScrollToBottom && (
          <TouchableOpacity
            style={[
              styles.scrollToBottomButton,
              { 
                right: Math.max(insets.right, 20),
                bottom: 80 + insets.bottom
              }
            ]}
            onPress={() => {
              if ((listRef.current as any)?.scrollToEnd) {
                try {
                  (listRef.current as any).scrollToEnd({ animated: true });
                } catch {
                  listRef.current?.scrollToOffset({ offset: 99999, animated: true });
                }
              } else {
                listRef.current?.scrollToOffset({ offset: 99999, animated: true });
              }
            }}
            activeOpacity={0.7}
          >
            <Ionicons name="arrow-down" size={24} color={theme.colors.card} />
          </TouchableOpacity>
        )}
        
        <InputBar 
          ref={inputBarRef}
          onSend={handleSend} 
          replyTarget={replyTarget}
          quoteTarget={quoteTarget}
          onCancelTarget={() => {
            setReplyTarget(null);
            setQuoteTarget(null);
          }}
          disableMedia={room.type === 'convo'}
        />
        {/* Bottom Safe Area Spacer - only visible when keyboard is hidden */}
        <View style={[styles.fixedBottomSpacer, { height: insets.bottom }]} />
      </KeyboardAvoidingView>

      {/* Talk Menu Modal */}
      <Modal
        visible={menuVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setMenuVisible(false)}
      >
        <TouchableOpacity 
          style={styles.menuOverlay} 
          activeOpacity={1} 
          onPress={() => setMenuVisible(false)}
        >
          <View style={[styles.menuContainer, { paddingTop: insets.top + 10, paddingBottom: insets.bottom }]}>
            <View style={styles.menuHeader}>
              <Text style={styles.menuTitle}>{room.title}</Text>
              <TouchableOpacity onPress={() => setMenuVisible(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>

            <View style={styles.menuGrid}>
              <TouchableOpacity 
                style={styles.menuGridItem}
                onPress={() => setIsMuted(!isMuted)}
              >
                <View style={styles.menuIconCircle}>
                  <Ionicons 
                    name={isMuted ? "notifications-off" : "notifications-outline"} 
                    size={24} 
                    color={isMuted ? theme.colors.primary : theme.colors.text} 
                  />
                </View>
                <Text style={[styles.menuGridLabel, isMuted && { color: theme.colors.primary }]}>
                  {isMuted ? t('talkRoom.notifOn') : t('talkRoom.notifOff')}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.menuGridItem}
                onPress={() => setShowMediaOnly(!showMediaOnly)}
              >
                <View style={styles.menuIconCircle}>
                  <Ionicons 
                    name={showMediaOnly ? "images" : "images-outline"} 
                    size={24} 
                    color={showMediaOnly ? theme.colors.primary : theme.colors.text} 
                  />
                </View>
                <Text style={[styles.menuGridLabel, showMediaOnly && { color: theme.colors.primary }]}>
                  {t('talkRoom.photosVideos')}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.menuGridItem}
                onPress={handleSearchInRoom}
              >
                <View style={styles.menuIconCircle}>
                  <Ionicons name="search-outline" size={24} color={theme.colors.text} />
                </View>
                <Text style={styles.menuGridLabel}>{t('common.search')}</Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.menuGridItem}
                onPress={() => {
                  setMenuVisible(false);
                  onSettings?.();
                }}
              >
                <View style={styles.menuIconCircle}>
                  <Ionicons name="settings-outline" size={24} color={theme.colors.text} />
                </View>
                <Text style={styles.menuGridLabel}>設定</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.menuList}>
              <TouchableOpacity 
                style={styles.menuListItem}
                onPress={() => {
                  setMenuVisible(false);
                  setColorPickerVisible(true);
                }}
              >
                <Ionicons name="color-palette-outline" size={20} color={theme.colors.text} style={styles.menuListIcon} />
                <Text style={styles.menuListText}>背景を変更</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Color Picker Modal */}
      <Modal
        visible={colorPickerVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setColorPickerVisible(false)}
      >
        <TouchableOpacity 
          style={styles.colorPickerOverlay} 
          activeOpacity={1} 
          onPress={() => setColorPickerVisible(false)}
        >
          <View style={styles.colorPickerContainer}>
            <Text style={styles.colorPickerTitle}>背景色を選択</Text>
            <View style={styles.colorGrid}>
              {[
                { name: 'デフォルト', color: '' },
                { name: 'ホワイト', color: '#FFFFFF' },
                { name: 'ブルー', color: '#7494C0' },
                { name: 'ライトブルー', color: '#E3F2FD' },
                { name: 'ライトグリーン', color: '#E8F5E9' },
                { name: 'ライトピンク', color: '#FCE4EC' },
                { name: 'ライトイエロー', color: '#FFFDE7' },
                { name: 'ダークグレー', color: '#424242' },
              ].map((item) => (
                <TouchableOpacity
                  key={item.color}
                  style={styles.colorOption}
                  onPress={async () => {
                    setBackgroundColor(item.color);
                    await AsyncStorage.setItem(CACHE_KEY_BACKGROUND, item.color);
                    setColorPickerVisible(false);
                  }}
                >
                  <View 
                    style={[
                      styles.colorSwatch, 
                      { backgroundColor: item.color || theme.colors.background },
                      item.color === backgroundColor && styles.colorSwatchSelected
                    ]} 
                  />
                  <Text style={styles.colorName}>{item.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity 
              style={styles.colorPickerClose}
              onPress={() => setColorPickerVisible(false)}
            >
              <Text style={styles.colorPickerCloseText}>キャンセル</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
    ...(Platform.OS === 'web' ? { height: '100%', overflow: 'hidden' } : {}),
  },
  flex: {
    flex: 1,
    overflow: 'hidden',
  },
  chatArea: {
    flex: 1,
    overflow: 'hidden',
  },
  messageScroller: {
    flex: 1,
  },
  messageList: {
    paddingVertical: 16,
  },
  fixedTopSpacer: {
    flexShrink: 0,
    backgroundColor: theme.colors.card,
  },
  fixedBottomSpacer: {
    flexShrink: 0,
    backgroundColor: theme.colors.background,
  },
  headerShell: {
    flexShrink: 0,
    backgroundColor: theme.colors.card,
    zIndex: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  menuOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  menuContainer: {
    width: '80%',
    height: '100%',
    backgroundColor: theme.colors.card,
  },
  menuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.cardSecondary,
  },
  menuTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  menuGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 10,
    backgroundColor: theme.colors.card,
    marginBottom: 10,
  },
  menuGridItem: {
    width: '25%',
    alignItems: 'center',
    paddingVertical: 15,
  },
  menuIconCircle: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: theme.colors.cardSecondary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  menuGridLabel: {
    fontSize: 11,
    color: theme.colors.textMuted,
  },
  menuList: {
    backgroundColor: theme.colors.card,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.border,
  },
  menuListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  menuListIcon: {
    marginRight: 15,
  },
  menuListText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  searchHeader: {
    flexShrink: 0,
    zIndex: 10,
    height: 50,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  searchBack: {
    padding: 4,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.input,
    borderRadius: 20,
    height: 36,
    paddingHorizontal: 12,
    marginLeft: 8,
    marginRight: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    // right and bottom will be set inline with safe area insets
    padding: 0,
  },
  scrollToBottomButton: {
    position: 'absolute',
    right: 20,
    bottom: 80, // Positioned above InputBar which is ~60-70px high
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 100,
  },
  colorPickerOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  colorPickerContainer: {
    backgroundColor: theme.colors.card,
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxWidth: 400,
  },
  colorPickerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 20,
    textAlign: 'center',
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  colorOption: {
    width: '23%',
    alignItems: 'center',
    marginBottom: 20,
  },
  colorSwatch: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: theme.colors.border,
  },
  colorSwatchSelected: {
    borderColor: theme.colors.primary,
    borderWidth: 3,
  },
  colorName: {
    fontSize: 11,
    color: theme.colors.textMuted,
    textAlign: 'center',
  },
  colorPickerClose: {
    marginTop: 10,
    paddingVertical: 12,
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 8,
    alignItems: 'center',
  },
  colorPickerCloseText: {
    fontSize: 16,
    color: theme.colors.text,
    fontWeight: '600',
  },
});

export default TalkRoom;
